/**
 * Dashboard Page
 *
 * Main overview page: clients that need a human (KAN-628), outreach status
 * (KAN-643), the funnel with per-stage cohort stats (KAN-737: one block instead
 * of the outreach funnel and the stage counts), sales per manager in the
 * e-commerce cabinet (KAN-783) and client contacts (TASK-208).
 * KAN-956: meetings and callbacks for today next to the attention block.
 */
import { useQuery } from '@tanstack/react-query'
import { LayoutDashboard } from 'lucide-react'
import { PageLayout } from '@/components/layout/PageLayout'
import { AttentionCard } from '@/components/dashboard/AttentionCard'
import { ExpensesSummaryBanner } from '@/components/dashboard/ExpensesSummaryBanner'
import { ContactsCard } from '@/components/dashboard/ContactsCard'
import { FunnelCard } from '@/components/dashboard/FunnelCard'
import { OutreachStatusCard } from '@/components/dashboard/OutreachStatusCard'
import { SalesByManagerCard } from '@/components/dashboard/SalesByManagerCard'
import { TodayStepsSection } from '@/components/todaySteps/TodayStepsSection'
import { useDialogParam } from '@/hooks/useDialogParam'
import { useOrgMode } from '@/hooks/useOrgMode'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { isManagerUser } from '@/lib/managerAccess'
import { dashboardApi, type Contact } from '@/services/api/dashboard'

/**
 * Loading spinner component
 */
function LoadingSpinner() {
  return (
    <div data-testid="dashboard-loading" className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
    </div>
  )
}

/**
 * Dashboard Page Component
 */
export function Dashboard() {
  const {
    data: contacts,
    isLoading: contactsLoading,
    error: contactsError,
  } = useQuery<Contact[]>({
    queryKey: ['dashboard-contacts'],
    queryFn: dashboardApi.getContacts,
  })
  // KAN-783: the sales block is an e-commerce report; hidden until the mode is known.
  const isEcommerce = useOrgMode() === 'ecommerce'
  // KAN-811: the sales report is closed to the MANAGER role (403), so no card and no request.
  const { data: user } = useCurrentUser()
  const showSales = isEcommerce && !isManagerUser(user)
  // KAN-956: a row of "today" opens the client's chat, like an attention row.
  const { openDialog } = useDialogParam()

  return (
    <PageLayout
      title="Дашборд"
      subtitle={<span className="hidden sm:inline">Обзор вашей организации</span>}
      titleIcon={
        <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
          <LayoutDashboard className="h-5 w-5 text-white" />
        </div>
      }
      breadcrumbs={[{ label: 'Главная' }]}
    >
      {/* KAN-735: owner-only expenses line; renders nothing on error or for others */}
      <ExpensesSummaryBanner />

      {/* KAN-628: clients that need a human, full width above the funnel */}
      <div className="mb-6">
        <AttentionCard />
      </div>

      {/* KAN-956/KAN-964: meetings and callbacks of the chosen day, overdue by button, month calendar on the right */}
      <div className="mb-6">
        <TodayStepsSection
          onOpen={(item) => openDialog({ clientId: item.client_id, hint: { title: item.name } })}
        />
      </div>

      {/* KAN-643: is Telegram outreach running today, above the funnel */}
      <div className="mb-6">
        <OutreachStatusCard />
      </div>

      {/* KAN-737: one funnel block, full width: the stage rows need the room */}
      <div className="mb-6">
        <FunnelCard />
      </div>

      {/* KAN-783: sales per manager, e-commerce cabinet only */}
      {showSales && (
        <div className="mb-6">
          <SalesByManagerCard />
        </div>
      )}

      {/* Client Contacts (TASK-208) */}
      {contactsLoading ? (
        <LoadingSpinner />
      ) : contactsError ? (
        <div data-testid="dashboard-error" className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-600 font-medium">Ошибка загрузки данных</p>
            <p className="text-gray-500 text-sm mt-1">Попробуйте позже</p>
          </div>
        </div>
      ) : (
        <ContactsCard contacts={contacts || []} />
      )}
    </PageLayout>
  )
}
